import type { NavigateFunction } from "react-router-dom"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"

import { GENERAL_CONFIG } from "../config/general.config"
import type { ProduktlyApi, ProduktlyMetadata } from "../types/produktly.types"
// Ensure global Window augmentation is registered
import "../types/produktly.types"
import { AuthHelper } from "./auth.helper"
import { UtilHelper } from "./util.helper"

export class ProduktlyHelper {
    /**
     * Identify the user for dashboard segmentation (practice, role, flags, etc.).
     * Clears any previous Produktly user first.
     * In-tour links use `navigate` for SPA navigation instead of a full reload.
     * Id is `<env>:user_<id>`, plus `:device_<uuid>` for a demo session. The
     * device uuid is created once in localStorage and kept across logout.
     * Values are coerced to string | number per Produktly API.
     */
    static identifyUser(
        userId: string | number,
        metadata: Record<string, string | number | boolean | null | undefined>,
        navigate: NavigateFunction,
        options?: { hash?: string },
    ): void {
        const instance = this.getInstance()

        if (!instance) {
            return
        }

        this.configure(instance, navigate)

        try {
            instance.identifyUser(
                this.buildUserId(userId),
                this.normalizeMetadata(metadata),
                options,
            )
        } catch (error) {
            console.error("Produktly identifyUser failed:", error)
        }
    }

    /**
     * Forget the current Produktly user (logout, or before identifying a
     * different user).
     */
    static clearUser(): void {
        try {
            this.getInstance()?.clearCurrentUser()
        } catch (error) {
            console.error("Produktly clearCurrentUser failed:", error)
        }
    }

    /**
     * Format a label or key into a stable `data-produktly` slug.
     * e.g. "Network Status" -> "network-status", "SMART_SCAN" -> "smart-scan"
     */
    static formatId(value: string | null | undefined): string {
        if (!value) {
            return ""
        }

        return value
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "")
    }

    /**
     * `<env>:user_<id>`. Demo sessions append `:device_<uuid>` so the shared
     * demo account is a distinct Produktly user per browser.
     */
    private static buildUserId(userId: string | number): string {
        const deviceSegment = AuthHelper.isDemoSessionActive()
            ? `:device_${this.getOrCreateDeviceId(userId)}`
            : ""

        const userSegment = `user_${userId}${deviceSegment}`

        const env = import.meta.env.VITE_ENV

        return `${env}:${userSegment}`
    }

    /**
     * Device uuid for demo identifies, per user. Stored as
     * `nirvana-produktlyDeviceId_<userId>` and intentionally not removed on logout.
     */
    private static getOrCreateDeviceId(userId: string | number): string {
        const storageKey = `${GENERAL_CONFIG.browserStorageKeys.produktlyDeviceId}_${userId}`

        const existing = BrowserStorageHelper.get<string>(
            storageKey,
            undefined,
            BrowserStorageType.localStorage,
        )

        if (typeof existing === "string" && existing.length > 0) {
            return existing
        }

        const deviceId = UtilHelper.generateUniqueVarchar()

        BrowserStorageHelper.set(
            storageKey,
            deviceId,
            BrowserStorageType.localStorage,
        )

        return deviceId
    }

    /**
     * Return the Produktly client, loading the script on first call. Null
     * outside sandbox, during Eagle Eye, or without a client token. Entering
     * and leaving Eagle Eye reloads the page, so the script is never loaded
     * while viewing as another user.
     */
    private static getInstance(): ProduktlyApi | null {
        if (!GENERAL_CONFIG.isSandbox || AuthHelper.getEagleEyeViewAsData()) {
            return null
        }

        if (!window.Produktly) {
            if (!import.meta.env.VITE_PRODUKTLY_CLIENT_TOKEN) {
                if (!GENERAL_CONFIG.isLocal) {
                    console.warn(
                        "Produktly client token not found. Produktly disabled.",
                    )
                }

                return null
            }

            try {
                this.installLoader()
            } catch (error) {
                console.error("Failed to load Produktly script:", error)

                return null
            }
        }

        return window.Produktly ?? null
    }

    /**
     * Loads Produktly asynchronously (same pattern as the dashboard snippet) so
     * calls queue until main.js is ready.
     * @see https://produktly.com/docs/docs/integration/examples
     */
    private static installLoader(): void {
        const win = window as Window & { ProduktlyQ?: unknown[] }

        win.Produktly =
            win.Produktly ||
            (new Proxy(
                function produktlyQueueTarget() {
                    /* target is never invoked; Produktly replaces window.Produktly after load */
                },
                {
                    get(_, prop) {
                        if (typeof prop !== "string") {
                            return undefined
                        }

                        return function (...args: unknown[]) {
                            win.ProduktlyQ = win.ProduktlyQ || []

                            win.ProduktlyQ.push([prop, args])
                        }
                    },
                    apply(_, __, argArray: unknown[]) {
                        win.ProduktlyQ = win.ProduktlyQ || []

                        win.ProduktlyQ.push(["apply", argArray])
                    },
                },
            ) as unknown as ProduktlyApi)

        if (document.getElementById(GENERAL_CONFIG.produktlyScriptId)) {
            return
        }

        const s = document.createElement("script")

        s.async = true

        s.src = GENERAL_CONFIG.produktlyScriptUrl

        s.id = GENERAL_CONFIG.produktlyScriptId

        s.dataset.clientToken = import.meta.env
            .VITE_PRODUKTLY_CLIENT_TOKEN as string // getInstance checks for token presence

        document.head.appendChild(s)
    }

    /**
     * Normalize metadata to ProduktlyMetadata type
     */
    private static normalizeMetadata(
        metadata: Record<string, string | number | boolean | null | undefined>,
    ): ProduktlyMetadata {
        const out: ProduktlyMetadata = {}

        for (const [key, value] of Object.entries(metadata)) {
            if (value === null || value === undefined) {
                continue
            }

            out[key] = typeof value === "boolean" ? Number(value) : value
        }

        return out
    }

    /**
     * SPA navigation for in-tour links (avoids full page reloads).
     */
    private static configure(
        instance: ProduktlyApi,
        navigate: NavigateFunction,
    ): void {
        try {
            instance.configure({
                redirect: url => {
                    try {
                        const next = new URL(url, window.location.origin)

                        if (next.origin === window.location.origin) {
                            navigate(
                                `${next.pathname}${next.search}${next.hash}`,
                            )
                        } else {
                            window.location.assign(url)
                        }
                    } catch {
                        navigate(url)
                    }
                },
            })
        } catch (error) {
            console.error("Produktly configure failed:", error)
        }
    }
}
